import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  fetchEngagementInsights,
  fetchViewsWatchTime,
  YouTubeAnalyticsPayload,
} from "../services/youtubeAnalytics";

interface Props {
  mode: "views" | "engagement";
}

const cardStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: "12px",
  padding: "16px",
  boxShadow: "0 8px 20px rgba(0,0,0,0.05)",
};

const demoData: Record<Props["mode"], YouTubeAnalyticsPayload> = {
  views: {
    summary: "Demo analytics for quick UX validation.",
    metrics: [
      { label: "Total Views", value: "182,430" },
      { label: "Watch Time (hours)", value: "6,912" },
      { label: "Avg View Duration", value: "2m 16s" },
      { label: "Views Last 7 Days", value: "24,510" },
    ],
  },
  engagement: {
    summary: "Demo engagement metrics for preview mode.",
    metrics: [
      { label: "Likes", value: "14,203" },
      { label: "Comments", value: "1,286" },
      { label: "Shares", value: "2,403" },
      { label: "Engagement Rate", value: "8.7%" },
    ],
  },
};

const YouTubeAnalyticsPanel: React.FC<Props> = ({ mode }) => {
  const { user, openAuthModal } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<YouTubeAnalyticsPayload | null>(null);
  const [isDemoMode, setIsDemoMode] = useState<boolean>(() => {
    return localStorage.getItem(`youtube_demo_mode_${mode}`) === "true";
  });

  useEffect(() => {
    localStorage.setItem(`youtube_demo_mode_${mode}`, String(isDemoMode));
  }, [isDemoMode, mode]);

  useEffect(() => {
    const load = async () => {
      if (isDemoMode) {
        setData(demoData[mode]);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const payload =
          mode === "views"
            ? await fetchViewsWatchTime(user?.sub)
            : await fetchEngagementInsights(user?.sub);
        setData(payload);
      } catch (err: any) {
        setError(err?.message || "Failed to fetch analytics.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isDemoMode, mode, user?.sub]);

  const title =
    mode === "views" ? "Views & Watch Time" : "Engagement Insights";

  const toggle = useMemo(
    () => (
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: "999px",
          padding: "8px 12px",
          fontSize: "13px",
          color: "#374151",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={isDemoMode}
          onChange={(e) => setIsDemoMode(e.target.checked)}
        />
        Demo Data Mode
      </label>
    ),
    [isDemoMode]
  );

  if (!user) {
    return (
      <div style={cardStyle}>
        <h2>{title}</h2>
        <p>Please sign in to view your connected YouTube channel analytics.</p>
        <button
          onClick={openAuthModal}
          style={{
            padding: "10px 16px",
            border: "none",
            borderRadius: "8px",
            background: "#714B67",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          gap: "12px",
        }}
      >
        <h2 style={{ color: "#714B67", margin: 0 }}>{title}</h2>
        {toggle}
      </div>

      {loading && <div style={cardStyle}>Loading analytics data...</div>}

      {!loading && error && (
        <div style={cardStyle}>
          <p style={{ color: "#B91C1C" }}>{error}</p>
          <button
            onClick={() => setIsDemoMode(true)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #D1D5DB",
              background: "#F9FAFB",
              cursor: "pointer",
            }}
          >
            Use Demo Data
          </button>
        </div>
      )}

      {!loading && !error && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(200px, 1fr))",
            gap: "14px",
          }}
        >
          {(data?.metrics ?? []).map((metric) => (
            <div key={metric.label} style={cardStyle}>
              <p style={{ margin: 0, color: "#6B7280", fontSize: "13px" }}>
                {metric.label}
              </p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: "28px",
                  fontWeight: 700,
                  color: "#1F2937",
                }}
              >
                {metric.value}
              </p>
              {metric.description && (
                <p style={{ margin: "10px 0 0", color: "#6B7280", fontSize: "12px" }}>
                  {metric.description}
                </p>
              )}
            </div>
          ))}

          {(!data?.metrics || data.metrics.length === 0) && (
            <div style={cardStyle}>
              <p>No metrics available yet for this section.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default YouTubeAnalyticsPanel;
